import { supabase } from '../supabaseClient';

export const flyersService = {
  async uploadFlyerPDF(file, editionYear) {
    try {
      // Create unique filename
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = `flyer-${timestamp}.${fileExt}`;
      const filePath = `${editionYear}/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('flyer-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('flyer-files')
        .getPublicUrl(filePath);

      return {
        path: filePath,
        url: publicUrl
      };
    } catch (error) {
      throw error;
    }
  },

  async uploadFlyerThumbnail(file, editionYear) {
    try {
      // Create unique filename
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = `flyer-thumb-${timestamp}.${fileExt}`;
      const filePath = `${editionYear}/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('flyer-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('flyer-images')
        .getPublicUrl(filePath);

      return {
        path: filePath,
        url: publicUrl
      };
    } catch (error) {
      throw error;
    }
  },

  async deleteFlyerFile(filePath, bucketName) {
    try {
      if (!filePath) return;

      const { error } = await supabase.storage
        .from(bucketName)
        .remove([filePath]);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  },

  async getFlyersData(editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Get page data
      const { data: pageData, error: pageError } = await supabase
        .from('flyers_page_data')
        .select('*')
        .eq('edition_id', edition.id)
        .single();

      if (pageError && pageError.code !== 'PGRST116') {
      }

      // Get flyers
      const { data: flyers } = await supabase
        .from('flyers')
        .select('*')
        .eq('edition_id', edition.id)
        .order('display_order');

      return {
        pageHeader: {
          title: pageData?.page_header_title || 'Nos Flyers',
          subtitle: pageData?.page_header_subtitle || '',
          emptyMessage: pageData?.page_header_empty_message || 'Aucun flyer disponible pour le moment.'
        },
        flyers: (flyers || []).map(flyer => ({
          id: flyer.id,
          title: flyer.title,
          description: flyer.description,
          fileName: flyer.file_name,
          downloadUrl: flyer.pdf_url,
          pdfPath: flyer.pdf_path,
          image: flyer.thumbnail_url,
          thumbnailPath: flyer.thumbnail_path,
          dateCreated: flyer.date_created,
          fileSize: flyer.file_size,
          displayOrder: flyer.display_order,
          showOnMedia: flyer.show_on_media === 'true' || flyer.show_on_media === true
        }))
      };
    } catch (error) {
      throw error;
    }
  },

  async updateFlyersPageData(pageData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const updateData = {
        edition_id: edition.id,
        page_header_title: pageData.title || 'Nos Flyers',
        page_header_subtitle: pageData.subtitle || '',
        page_header_empty_message: pageData.emptyMessage || 'Aucun flyer disponible pour le moment.',
        updated_at: new Date().toISOString()
      };

      // Try to update existing record first
      const { data: existingData } = await supabase
        .from('flyers_page_data')
        .select('id')
        .eq('edition_id', edition.id)
        .single();

      if (existingData) {
        // Update existing record
        const { data, error } = await supabase
          .from('flyers_page_data')
          .update(updateData)
          .eq('id', existingData.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('flyers_page_data')
          .insert([updateData])
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      throw error;
    }
  },

  async addFlyer(flyerData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const insertData = {
        edition_id: edition.id,
        title: flyerData.title,
        description: flyerData.description,
        file_name: flyerData.fileName,
        pdf_path: flyerData.pdfPath || '',
        pdf_url: flyerData.downloadUrl,
        thumbnail_path: flyerData.thumbnailPath || '',
        thumbnail_url: flyerData.image,
        file_size: flyerData.fileSize || '',
        date_created: flyerData.dateCreated || new Date().toISOString().split('T')[0],
        display_order: flyerData.displayOrder || 0,
        show_on_media: flyerData.showOnMedia ? 'true' : 'false'
      };

      const { data, error } = await supabase
        .from('flyers')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async updateFlyer(flyerId, flyerData) {
    try {
      const updateData = {
        title: flyerData.title,
        description: flyerData.description,
        file_name: flyerData.fileName,
        pdf_path: flyerData.pdfPath || '',
        pdf_url: flyerData.downloadUrl,
        thumbnail_path: flyerData.thumbnailPath || '',
        thumbnail_url: flyerData.image,
        file_size: flyerData.fileSize || '',
        display_order: flyerData.displayOrder || 0,
        show_on_media: flyerData.showOnMedia ? 'true' : 'false',
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('flyers')
        .update(updateData)
        .eq('id', flyerId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw error;
    }
  },

  async deleteFlyer(flyerId) {
    try {
      // Get flyer data first to delete files
      const { data: flyer } = await supabase
        .from('flyers')
        .select('pdf_path, thumbnail_path')
        .eq('id', flyerId)
        .single();

      if (flyer) {
        // Delete files from storage
        if (flyer.pdf_path) {
          await this.deleteFlyerFile(flyer.pdf_path, 'flyer-files');
        }
        if (flyer.thumbnail_path) {
          await this.deleteFlyerFile(flyer.thumbnail_path, 'flyer-images');
        }
      }

      // Delete record from database
      const { error } = await supabase
        .from('flyers')
        .delete()
        .eq('id', flyerId);

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  }
};
